/**
 * Failure-signature rules over the parsed `log.summary` jsonb.
 *
 * Everything here runs on numbers the backend already extracted — no extra
 * fetch, no new endpoint. A rule fires only on evidence present in the log; the
 * `hint` is the physical thing to go look at, which is the part a chart can't
 * tell you.
 *
 * Thresholds are config, not code: a tail-sitter's normal attitude error during
 * transition is a copter's emergency. The numbers below are what ships; an
 * operator retunes them in the Thresholds tab and those edits merge on top at
 * evaluation time.
 */

const BASE = {
  satsMin: 8, // below this, position solution is fragile
  hdopMax: 2.0,
  vibeWarn: 15, // m/s/s — ArduPilot's own "investigate" line is ~30
  vibeCrit: 30,
  attErrWarn: 10, // deg between demanded and achieved attitude
  attErrCrit: 25,
  cellWarn: 3.6, // volts per cell under load
  cellCrit: 3.3,
};

/** Per-airframe overrides, matched loosely against `log.vehicle_type`. */
export const PROFILES = {
  plane: { attErrWarn: 15, attErrCrit: 35 },
  // Thrust-vectored tail-sitter: transition is a commanded 90° attitude change,
  // so tracking error spikes are expected there and only matter if sustained.
  tailsitter: { attErrWarn: 25, attErrCrit: 45, vibeWarn: 20 },
};

/* ------------------------------------------------------- operator tuning -- */

/**
 * What the Thresholds tab renders, in the order it renders it. `rule` names the
 * finding a number feeds, so whoever moves it can see what they are moving.
 */
export const THRESHOLD_FIELDS = [
  {
    key: "satsMin",
    label: "Minimum satellites",
    unit: "sats",
    step: 1,
    rule: "Marginal GPS quality",
    why: "Below this the position solution is fragile.",
  },
  {
    key: "hdopMax",
    label: "Maximum HDOP",
    unit: "",
    step: 0.1,
    rule: "Marginal GPS quality",
    why: "Higher is worse geometry. Degraded geometry inflates position error.",
  },
  {
    key: "vibeWarn",
    label: "Vibration — warn",
    unit: "m/s²",
    step: 1,
    rule: "High vibration",
    why: "ArduPilot's own investigate line is around 30.",
  },
  { key: "vibeCrit", label: "Vibration — critical", unit: "m/s²", step: 1, rule: "High vibration" },
  {
    key: "attErrWarn",
    label: "Attitude error — warn",
    unit: "°",
    step: 1,
    rule: "Attitude tracking error",
    why: "Degrees between demanded and achieved attitude, at peak.",
  },
  { key: "attErrCrit", label: "Attitude error — critical", unit: "°", step: 1, rule: "Attitude tracking error" },
  {
    key: "cellWarn",
    label: "Cell voltage — warn",
    unit: "V/cell",
    step: 0.05,
    rule: "Battery voltage sag",
    why: "Under load, per cell. Lower is worse here, so critical sits below warn.",
  },
  { key: "cellCrit", label: "Cell voltage — critical", unit: "V/cell", step: 0.05, rule: "Battery voltage sag" },
];

/** warn/crit pairs, and which direction is the worse one. */
export const THRESHOLD_PAIRS = [
  { warn: "vibeWarn", crit: "vibeCrit", worse: "higher" },
  { warn: "attErrWarn", crit: "attErrCrit", worse: "higher" },
  { warn: "cellWarn", crit: "cellCrit", worse: "lower" },
];

const PROFILE_LABELS = { plane: "Plane", tailsitter: "Tail-sitter" };

/** Tunable groups: the set every airframe starts from, then each profile. */
export const THRESHOLD_GROUPS = [
  { key: "base", label: "All airframes" },
  ...Object.keys(PROFILES).map((key) => ({ key, label: PROFILE_LABELS[key] || key })),
];

/**
 * A config where critical sits on the safe side of warn would silently
 * downgrade every hit on that pair, so the editor blocks it rather than
 * storing it. Returns { [key]: message } — empty when the set is coherent.
 */
export const validateThresholds = (t = {}) => {
  const errors = {};
  for (const { warn, crit, worse } of THRESHOLD_PAIRS) {
    if (t[warn] == null || t[crit] == null) continue;
    const higher = worse === "higher";
    if (higher ? t[crit] < t[warn] : t[crit] > t[warn]) {
      errors[crit] = higher ? "Critical must be at or above warn." : "Critical must be at or below warn.";
    }
  }
  return errors;
};

/* --------------------------------------------------------- persistence -- */

const STORAGE_KEY = "log-analyzer:thresholds";

const GROUP_KEYS = THRESHOLD_GROUPS.map((g) => g.key);

/**
 * Only known keys carrying a finite positive number survive. Stored config is
 * hand-editable and outlives this build, so a stale or corrupt entry must fall
 * back to the shipped number rather than feed NaN into a comparison — a rule
 * that silently stops firing is worse than one that was never tuned.
 */
const sanitize = (patch) =>
  Object.fromEntries(
    Object.entries(patch || {}).filter(
      ([k, v]) => k in BASE && typeof v === "number" && Number.isFinite(v) && v > 0
    )
  );

/** Sparse: only what was deliberately changed, so retuned defaults still land. */
export const loadOverrides = () => {
  let raw;
  try {
    raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return {}; // corrupt or unavailable storage — the shipped numbers stand
  }
  if (!raw || typeof raw !== "object") return {};
  return Object.fromEntries(
    GROUP_KEYS.map((g) => [g, sanitize(raw[g])]).filter(([, patch]) => Object.keys(patch).length)
  );
};

export const saveOverrides = (overrides) => {
  const clean = Object.fromEntries(
    GROUP_KEYS.map((g) => [g, sanitize(overrides?.[g])]).filter(([, patch]) => Object.keys(patch).length)
  );
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
  } catch {
    // Private browsing or a full quota. Nothing is lost that the shipped
    // defaults do not already cover, and the RCA export states what was used.
  }
  return clean;
};

/* ----------------------------------------------------------- resolution -- */

const profileKey = (log) => {
  const v = (log?.vehicle_type || "").toLowerCase();
  return Object.keys(PROFILES).find((k) => v.includes(k)) || null;
};

/** What ships for one tunable group, before any local tuning. */
export const shippedForGroup = (group) => ({ ...BASE, ...(PROFILES[group] || {}) });

/**
 * Effective thresholds for one group: shipped values, then the operator's
 * edits, with the airframe profile staying more specific than the
 * all-airframes set — the same precedence the profiles already had.
 */
export const thresholdsForGroup = (group, overrides = loadOverrides()) => ({
  ...BASE,
  ...overrides.base,
  ...(PROFILES[group] ? { ...PROFILES[group], ...overrides[group] } : {}),
});

export const defaultThresholdsFor = (log) => shippedForGroup(profileKey(log) || "base");

export const thresholdsFor = (log, overrides = loadOverrides()) =>
  thresholdsForGroup(profileKey(log) || "base", overrides);

/**
 * Every threshold whose effective value differs from what shipped, labelled the
 * way the Thresholds tab labels it so the panel, the report, and the editor all
 * name the same number the same way. Empty on an untouched config.
 */
export const tunedThresholds = (log) => {
  const now = thresholdsFor(log);
  const shipped = defaultThresholdsFor(log);
  return THRESHOLD_FIELDS.filter((f) => now[f.key] !== shipped[f.key]).map((f) => ({
    key: f.key,
    label: f.label,
    unit: f.unit,
    value: now[f.key],
    shipped: shipped[f.key],
  }));
};

const SEVERITY_RANK = { critical: 0, warn: 1, info: 2 };

// Modes an aircraft enters on its own when something has gone wrong.
const RECOVERY_MODES = { 6: "RTL", 9: "Land", 21: "Smart RTL" };

const peakVibe = (vib = {}) =>
  Math.max(vib.vibex_max ?? 0, vib.vibey_max ?? 0, vib.vibez_max ?? 0);

const maxAttErr = (att = {}) =>
  Math.max(att.roll_err_max_deg ?? 0, att.pitch_err_max_deg ?? 0, att.yaw_err_max_deg ?? 0);

/** Pack cell count inferred from the starting voltage — LiPo, ~4.1 V/cell hot off the charger. */
const cellCount = (battery = {}) => {
  if (battery.cells) return battery.cells;
  if (!battery.volt_start) return null;
  return Math.max(1, Math.round(battery.volt_start / 4.1));
};

/**
 * Each rule reads the summary and returns null (nothing to say) or a finding.
 * `evidence` is the quantitative claim; `hint` is where to look on the aircraft.
 */
const RULES = [
  {
    id: "truncated",
    title: "Log ends mid-flight",
    check: (s) =>
      s.truncated
        ? {
            severity: "critical",
            evidence: "Log has no clean shutdown record — the last write is abrupt.",
            hint: "Power interruption, loose battery lead, or the airframe stopped writing on impact. Inspect the power path and the SD card seating before flying again.",
          }
        : null,
  },
  {
    id: "gps_fix_lost",
    title: "Lost 3D GPS fix",
    check: (s) =>
      s.gps?.fix_min != null && s.gps.fix_min < 3
        ? {
            severity: "critical",
            evidence: `Fix type dropped to ${s.gps.fix_min} (3 = 3D lock).`,
            hint: "Position-dependent modes cannot hold without a 3D fix. Check GPS mast height, antenna ground plane, and RF noise from ESC/video leads routed near the receiver.",
          }
        : null,
  },
  {
    id: "gps_quality",
    title: "Marginal GPS quality",
    check: (s, log, T) => {
      const sats = s.gps?.sats_min;
      const hdop = s.gps?.hdop_max;
      const bad = (sats != null && sats < T.satsMin) || (hdop != null && hdop > T.hdopMax);
      if (!bad) return null;
      const parts = [];
      if (sats != null) parts.push(`min ${sats} satellites`);
      if (hdop != null) parts.push(`peak HDOP ${hdop}`);
      return {
        severity: "warn",
        evidence: `${parts.join(", ")} (limits: ${T.satsMin} sats, HDOP ${T.hdopMax}).`,
        hint: "Degraded geometry inflates position error and can push the EKF into velocity-only navigation. Check for obstructions at the launch site and interference from nearby electronics.",
      };
    },
  },
  {
    id: "accel_clip",
    title: "Accelerometer clipping",
    check: (s) =>
      s.vibration?.clip_max > 0
        ? {
            severity: "critical",
            evidence: `${s.vibration.clip_max} clipping events — the IMU saturated its measurement range.`,
            hint: "Clipped accelerometers feed garbage into the EKF and cause altitude and position drift. Inspect prop balance, motor bearings, and the flight-controller anti-vibration mount.",
          }
        : null,
  },
  {
    id: "vibration",
    title: "High vibration",
    check: (s, log, T) => {
      const peak = peakVibe(s.vibration);
      if (!peak || peak < T.vibeWarn) return null;
      return {
        severity: peak >= T.vibeCrit ? "critical" : "warn",
        evidence: `Peak vibration ${peak.toFixed(1)} m/s² (warn ${T.vibeWarn}, critical ${T.vibeCrit}).`,
        hint: "Usually a damaged or unbalanced prop, a worn motor bearing, or a loose arm. Spin each motor by hand and check for play before the next flight.",
      };
    },
  },
  {
    id: "attitude_tracking",
    title: "Attitude tracking error",
    check: (s, log, T) => {
      const err = maxAttErr(s.attitude);
      if (!err || err < T.attErrWarn) return null;
      return {
        severity: err >= T.attErrCrit ? "critical" : "warn",
        evidence: `Demanded vs achieved attitude diverged by ${err.toFixed(1)}° (warn ${T.attErrWarn}°, critical ${T.attErrCrit}°).`,
        hint: "The controller asked for something the airframe did not deliver: saturated or failed actuator, loss of thrust on one motor, or tuning that cannot hold the commanded rate. Check actuator travel and motor output balance over the same window.",
      };
    },
  },
  {
    id: "battery_sag",
    title: "Battery voltage sag",
    check: (s, log, T) => {
      const cells = cellCount(s.battery);
      const vmin = s.battery?.volt_min;
      if (!cells || vmin == null) return null;
      const perCell = vmin / cells;
      if (perCell >= T.cellWarn) return null;
      return {
        severity: perCell <= T.cellCrit ? "critical" : "warn",
        evidence: `Minimum ${vmin} V across an inferred ${cells}S pack = ${perCell.toFixed(2)} V/cell (warn ${T.cellWarn}, critical ${T.cellCrit}).`,
        hint: "Either the pack is worn, undersized for the current draw, or the flight ran past its reserve. Check pack internal resistance and per-cell balance on the bench.",
      };
    },
  },
  {
    id: "logged_errors",
    title: "Autopilot logged subsystem errors",
    check: (s) => {
      const errors = s.errors || [];
      if (!errors.length) return null;
      const listed = errors
        .slice(0, 5)
        .map((e) => `subsystem ${e.subsys}/code ${e.ecode}`)
        .join("; ");
      return {
        severity: "critical",
        at_us: errors[0].t_us,
        evidence: `${errors.length} ERR message${errors.length > 1 ? "s" : ""}: ${listed}${errors.length > 5 ? "; …" : ""}.`,
        hint: "The firmware itself flagged these. Resolve the subsystem codes against the ArduPilot error table and correlate each timestamp with the flight timeline below.",
      };
    },
  },
  {
    id: "autonomous_recovery",
    title: "Aircraft entered a recovery mode",
    check: (s) => {
      const modes = s.modes || [];
      // The first mode entry is the boot mode, not a decision made in flight.
      const hit = modes.slice(1).find((m) => RECOVERY_MODES[m.mode]);
      if (!hit) return null;
      return {
        severity: "warn",
        at_us: hit.t_us,
        evidence: `Switched to ${RECOVERY_MODES[hit.mode]} during the flight.`,
        hint: "If the pilot did not command this, a failsafe fired. Establish which trigger (RC loss, GCS loss, battery, EKF) and whether the response matched the intended behaviour.",
      };
    },
  },
];

/**
 * Run every rule against a parsed log. Returns findings sorted worst-first.
 * A log that is still parsing, failed, or carries no summary yields [].
 */
export const evaluateFindings = (log) => {
  const s = log?.summary;
  if (!s || log.status !== "ready") return [];
  const T = thresholdsFor(log);
  return RULES.map((rule) => {
    const hit = rule.check(s, log, T);
    return hit ? { id: rule.id, title: rule.title, ...hit } : null;
  })
    .filter(Boolean)
    .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
};

export const worstSeverity = (findings) =>
  findings.reduce(
    (worst, f) => (SEVERITY_RANK[f.severity] < SEVERITY_RANK[worst] ? f.severity : worst),
    "info"
  );

/**
 * A tuned threshold changes which rules fire, so a report that does not say so
 * cannot be audited later. Silent on an untouched config.
 */
const thresholdNote = (log) => {
  const tuned = tunedThresholds(log);
  if (!tuned.length) return [];
  const list = tuned.map((t) => `${t.label} ${t.value} (ships as ${t.shipped})`).join("; ");
  return [`- **Thresholds:** locally tuned — ${list}`];
};

const clock = (us, baseUs) => {
  if (us == null || baseUs == null) return "";
  const total = Math.max(0, Math.round((us - baseUs) / 1e6));
  return `+${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/**
 * Markdown RCA draft: the facts, the evidence, and what to inspect. Written to
 * be pasted into an incident record and edited — the conclusion is still a
 * human's to sign.
 *
 * `sections` are extra markdown blocks (parameter audit, inspection record)
 * composed by the caller, so this module stays unaware of where they came from.
 */
export const buildRcaReport = (log, findings = evaluateFindings(log), { sections = [] } = {}) => {
  const s = log?.summary || {};
  const timeline = [
    ...(s.modes || []).map((m) => ({ t_us: m.t_us, text: `Mode ${m.mode}` })),
    ...(s.errors || []).map((e) => ({ t_us: e.t_us, text: `ERR subsystem ${e.subsys} code ${e.ecode}` })),
    ...(s.events || []).map((e) => ({ t_us: e.t_us, text: `Event ${e.id}` })),
  ]
    .filter((e) => e.t_us != null)
    .sort((a, b) => a.t_us - b.t_us);
  const baseUs = timeline.length ? timeline[0].t_us : null;

  const lines = [
    `# Flight log RCA — ${log?.file_name || "untitled"}`,
    "",
    `- **Vehicle:** ${log?.vehicle_type || "unknown"}`,
    `- **Firmware:** ${s.firmware || "unknown"}`,
    `- **Duration:** ${s.duration_s != null ? `${Math.round(s.duration_s)} s` : "unknown"}`,
    `- **Automated findings:** ${findings.length} (worst: ${findings.length ? worstSeverity(findings) : "none"})`,
    ...thresholdNote(log),
    "",
    "## Findings",
    "",
  ];

  if (!findings.length) {
    lines.push("No rule fired. Nothing in the extracted summary crossed a threshold — this is not a statement that the flight was nominal, only that these checks found nothing.", "");
  } else {
    findings.forEach((f, i) => {
      lines.push(
        `### ${i + 1}. ${f.title} — ${f.severity}${f.at_us != null ? ` (${clock(f.at_us, baseUs)})` : ""}`,
        "",
        `**Evidence:** ${f.evidence}`,
        "",
        `**Physical check:** ${f.hint}`,
        ""
      );
    });
  }

  lines.push("## Timeline", "");
  if (timeline.length) {
    timeline.forEach((e) => lines.push(`- \`${clock(e.t_us, baseUs)}\` ${e.text}`));
  } else {
    lines.push("No mode changes, events, or errors recorded.");
  }
  lines.push("");
  sections.filter(Boolean).forEach((section) => lines.push(section.trimEnd(), ""));
  lines.push(
    "## Root cause",
    "",
    "_Hypothesis, the evidence for and against it, and what was ruled out. Fill in after inspection._",
    "",
    "## Corrective action",
    "",
    "_Parameter changes, hardware replaced, or procedure updated._",
    ""
  );
  return lines.join("\n");
};
